/**
 * What a "sent" screen may honestly claim about an invitation.
 *
 * The contact fields on the Configure step ARE the channel choice: a
 * filled field means that channel is attempted, an empty one means it
 * is not. The API enforces the other half — its validator rejects a
 * filled field the patient cannot receive (`EmailOptInDisabled` /
 * `SMSOptInDisabled`), so to send at all the user has to clear that
 * field. By the time the Done step renders, the filled fields are
 * exactly the channels that were attempted, including anything the
 * user typed by hand rather than taking from the patient record.
 *
 * "Attempted", not "delivered": the server queues both sends inside a
 * try/catch and never reports the outcome back, so no screen in the
 * dashboard can know whether anything arrived.
 */

export const DELIVERY_CHANNEL = { email: 'email', sms: 'sms' };

const filled = value => (typeof value === 'string' ? value.trim() : '');

/**
 * The channels this invitation goes out on, in reading order.
 * Returns `[{ channel, address }]`, empty when neither field is set.
 */
export const resolveDeliveryChannels = ({ email, phone } = {}) => {
  const channels = [];
  const emailAddress = filled(email);
  const phoneNumber = filled(phone);
  if (emailAddress) channels.push({ channel: DELIVERY_CHANNEL.email, address: emailAddress });
  if (phoneNumber) channels.push({ channel: DELIVERY_CHANNEL.sms, address: phoneNumber });
  return channels;
};

/**
 * The badge glyph for the success tile. SMS-only invitations were
 * showing an envelope.
 */
export const deliveryBadge = channels =>
  channels?.length === 1 && channels[0].channel === DELIVERY_CHANNEL.sms ? 'sms' : 'mail';

/**
 * The scheduled day as the Configure step shows it. Both panels keep
 * `scheduledDate` as the "MM/DD/YYYY" string `DateField` emits (the
 * submit helper splits it on "/"), so the string is the display value;
 * a Date is formatted the same way for callers that hold one. Null for
 * anything else, so the row is dropped rather than a crash: the Done
 * screen used to hand the string to a Date-only formatter, which threw
 * on `getFullYear` and sent every scheduled send to the error page.
 */
export const formatScheduleDate = value => {
  if (typeof value === 'string') return value.trim() || null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${month}/${day}/${value.getFullYear()}`;
  }
  return null;
};

/** Anything other than "send now" is scheduled, including an absent value. */
export const isScheduledSend = sendingOption => !!sendingOption && sendingOption !== 'sendNow';

/**
 * Minutes since midnight -> "5:15 PM", matching what `TimeField`
 * displays on the Configure step. Null for a value that field would
 * itself refuse (it stores the string 'invalid_value' for one).
 */
export const formatScheduleTime = minutes => {
  // Before the coercion: `Number(null)` and `Number('')` are both 0,
  // which would print a missing time as midnight.
  if (minutes === null || minutes === undefined || minutes === '') return null;
  const total = Number(minutes);
  if (!Number.isFinite(total) || total < 0 || total >= 24 * 60) return null;
  const hours24 = Math.floor(total / 60);
  const mins = Math.floor(total % 60);
  const meridiem = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${String(mins).padStart(2, '0')} ${meridiem}`;
};

export default resolveDeliveryChannels;
